import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Save, Edit2, Eye, EyeOff, X } from 'lucide-react';

interface AIPrompt {
  id: string;
  category: string;
  name: string;
  description: string | null;
  prompt_text: string;
  model: string;
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function AdminPrompts() {
  const { isAdmin } = useAuth();
  const [prompts, setPrompts] = useState<AIPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<AIPrompt>>({});
  const [filter, setFilter] = useState<string>('all');
  const [saveStatus, setSaveStatus] = useState<string>('');

  useEffect(() => {
    if (isAdmin) {
      loadPrompts();
    }
  }, [isAdmin]);

  async function loadPrompts() {
    setLoading(true);
    const { data, error } = await supabase
      .from('ai_prompts')
      .select('*')
      .order('category', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      console.error('Error loading prompts:', error);
    } else {
      setPrompts(data || []);
    }
    setLoading(false);
  }

  async function savePrompt() {
    if (!editingId || !editForm.prompt_text) return;

    setSaveStatus('Saving...');
    const { error } = await supabase
      .from('ai_prompts')
      .update({
        prompt_text: editForm.prompt_text,
        description: editForm.description,
        model: editForm.model,
        is_active: editForm.is_active,
        updated_at: new Date().toISOString()
      })
      .eq('id', editingId);

    if (error) {
      setSaveStatus('Error saving');
      console.error('Error saving prompt:', error);
    } else {
      setSaveStatus('Saved!');
      setEditingId(null);
      setEditForm({});
      await loadPrompts();
      setTimeout(() => setSaveStatus(''), 2000);
    }
  }

  async function toggleActive(id: string, currentStatus: boolean) {
    await supabase
      .from('ai_prompts')
      .update({ is_active: !currentStatus })
      .eq('id', id);

    await loadPrompts();
  }

  function startEditing(prompt: AIPrompt) {
    setEditingId(prompt.id);
    setEditForm(prompt);
  }

  function cancelEditing() {
    setEditingId(null);
    setEditForm({});
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  const categories = ['all', ...new Set(prompts.map(p => p.category))];
  const filteredPrompts = filter === 'all'
    ? prompts
    : prompts.filter(p => p.category === filter);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Prompt Management</h1>
          <p className="text-gray-600">Edit and manage all AI prompts across the platform</p>
        </div>

        {saveStatus && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
            {saveStatus}
          </div>
        )}

        <div className="mb-6 flex gap-2 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === cat
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {cat.replace(/_/g, ' ').toUpperCase()}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPrompts.map(prompt => (
              <div
                key={prompt.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                          {prompt.category.replace(/_/g, ' ')}
                        </span>
                        <h3 className="text-lg font-bold text-gray-900">{prompt.name}</h3>
                        {!prompt.is_active && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded">
                            INACTIVE
                          </span>
                        )}
                      </div>
                      {editingId === prompt.id ? (
                        <input
                          type="text"
                          value={editForm.description || ''}
                          onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Description"
                        />
                      ) : (
                        <p className="text-gray-600 text-sm">{prompt.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {editingId === prompt.id ? (
                        <>
                          <button
                            onClick={savePrompt}
                            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            title="Save"
                          >
                            <Save className="w-5 h-5" />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                            title="Cancel"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditing(prompt)}
                            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                            title="Edit"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => toggleActive(prompt.id, prompt.is_active)}
                            className={`p-2 rounded-lg transition ${
                              prompt.is_active
                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            }`}
                            title={prompt.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {prompt.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mb-4 flex gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Model:</span>
                      {editingId === prompt.id ? (
                        <input
                          type="text"
                          value={editForm.model || ''}
                          onChange={e => setEditForm({ ...editForm, model: e.target.value })}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      ) : (
                        <span className="ml-2 font-medium text-gray-900">{prompt.model}</span>
                      )}
                    </div>
                    <div>
                      <span className="text-gray-500">Version:</span>
                      <span className="ml-2 font-medium text-gray-900">{prompt.version}</span>
                    </div>
                  </div>

                  {editingId === prompt.id ? (
                    <textarea
                      value={editForm.prompt_text || ''}
                      onChange={e => setEditForm({ ...editForm, prompt_text: e.target.value })}
                      rows={12}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                        {prompt.prompt_text}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
